import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";

import { Button, IconButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/stores/locale";

/**
 * Every overlay in the app is a native `<dialog>`.
 *
 * The platform gives the focus trap, the return of focus on close, Escape, and
 * inertness of the page behind — four things that are easy to get wrong and
 * unforgivable to get wrong. All this adds is the shape.
 */
function useModal(open: boolean, onClose: () => void, dismissible = true) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  // Escape closes a native `<dialog>` by itself, and hiding the X does not
  // stop it. A dialog that must not be dismissed has to refuse here too, or
  // `dismissible={false}` is a suggestion with a keyboard shortcut past it.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || dismissible) return;
    const refuse = (event: Event) => event.preventDefault();
    dialog.addEventListener("cancel", refuse);
    return () => dialog.removeEventListener("cancel", refuse);
  }, [dismissible]);
  // `close` fires for Escape and for the form's dialog method too, so the
  // caller's state can never drift out of step with what is on screen.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => onClose();
    dialog.addEventListener("close", handle);
    return () => dialog.removeEventListener("close", handle);
  }, [onClose]);
  return ref;
}

/** Clicking the backdrop is a click on the dialog element itself, not its content. */
const dismissOnBackdrop =
  (onClose: () => void) => (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

const SIZE = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  /**
   * A table's worth of room: the width of the list screens, and wider when the
   * content needs it, up to the viewport.
   *
   * For work whose size is not ours to pick — a grid of a file, two columns for
   * one update and twenty for another. Growing from nothing would make a
   * two-column file a narrow strip; the floor is what the list it came from
   * looks like. The height is the content's, so no empty half-page below it.
   */
  fit: "w-fit min-w-[min(80rem,calc(100vw-4rem))] max-w-[calc(100vw-4rem)] max-h-[calc(100dvh-4rem)]",
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title. Anything longer belongs in the body. */
  description?: ReactNode;
  size?: keyof typeof SIZE;
  /** The actions row. Cancel on the left of the primary action, always. */
  footer?: ReactNode;
  /** A decision that has to be made: no X, no backdrop click, no Escape. */
  dismissible?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * A modal is for one short, focused task. A whole page inside a modal is a page
 * that should have stayed a page — use a Drawer for context, a route for work.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = "md",
  footer,
  dismissible = true,
  className,
  children,
}: ModalProps) {
  const ref = useModal(open, onClose, dismissible);
  const titleId = useId();
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClick={dismissible ? dismissOnBackdrop(onClose) : undefined}
      className={cn(
        "animate-sheet bg-surface-raised text-fg m-auto w-[calc(100%-2rem)] p-0",
        "rounded-sheet max-h-[min(85dvh,44rem)] overflow-hidden shadow-[var(--shadow-sheet)]",
        "grid grid-rows-[auto_minmax(0,1fr)_auto]",
        SIZE[size],
        className,
      )}
    >
      <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-title-3 text-heading">
            {title}
          </h2>
          {description ? (
            <p className="text-fg-muted text-subhead mt-1 text-pretty">{description}</p>
          ) : null}
        </div>
        {dismissible ? (
          <IconButton label="Close" size="icon-sm" className="-mt-1 -mr-2" onClick={onClose}>
            <X aria-hidden />
          </IconButton>
        ) : (
          <span />
        )}
      </header>

      <div className="overflow-y-auto px-6 pb-1">{children}</div>

      {footer ? (
        <footer className="hairline-t mt-4 flex items-center justify-end gap-2.5 px-6 py-4">
          {footer}
        </footer>
      ) : (
        <div className="h-5" />
      )}
    </dialog>
  );
}

/**
 * A drawer keeps the page behind it visible, which is the whole point: it is
 * for looking at, or adjusting, something in the context of the list it came
 * from. Filters, a record preview, a quick edit.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  width = "md",
  className,
  children,
}: Omit<ModalProps, "size" | "dismissible"> & { width?: "sm" | "md" | "lg" }) {
  const ref = useModal(open, onClose);
  const titleId = useId();
  const widths = { sm: "sm:max-w-sm", md: "sm:max-w-md", lg: "sm:max-w-xl" } as const;
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClick={dismissOnBackdrop(onClose)}
      className={cn(
        "animate-drawer bg-surface-raised text-fg fixed inset-y-0 right-0 left-auto",
        "m-0 h-dvh max-h-none w-full rounded-none p-0 shadow-[var(--shadow-sheet)]",
        "grid grid-rows-[auto_minmax(0,1fr)_auto]",
        widths[width],
        className,
      )}
    >
      <header className="hairline-b flex items-start justify-between gap-4 px-5 pt-5 pb-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-title-3 text-heading">
            {title}
          </h2>
          {description ? (
            <p className="text-fg-muted text-subhead mt-1">{description}</p>
          ) : null}
        </div>
        <IconButton label="Close" size="icon-sm" className="-mt-1 -mr-2" onClick={onClose}>
          <X aria-hidden />
        </IconButton>
      </header>

      {/* `relative`: Tailwind's `sr-only` is `position: absolute`, so without a
          positioned ancestor a hidden label resolves against the document and
          stretches it. The same rule as the list table's scroll box. */}
      <div className="relative overflow-y-auto px-5 py-5">{children}</div>

      {footer ? (
        <footer className="hairline-t flex items-center justify-end gap-2.5 px-5 py-4">
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}

/**
 * A confirmation, for a consequence that is real and hard to walk back.
 *
 * Harmless actions do not get one — a dialog nobody reads is a dialog that
 * trains people not to read dialogs. The title asks the question, the body says
 * what it costs, and the button says what it does rather than "OK".
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
  loading?: boolean;
}) {
  const t = useTranslation();
  confirmLabel ??= t("common.delete");
  cancelLabel ??= t("common.cancel");
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
            loadingLabel={confirmLabel}
            autoFocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-fg-muted text-callout text-pretty">{message}</p>
    </Modal>
  );
}
